import {Injectable} from '@angular/core';
import {UserModel} from '../models/user.model';
@Injectable({
  providedIn: 'root'
})
export class Session {
  private static lastVisitedUrl = '';
  private static SESSION_TTL = 12 * 60 * 60 * 1000; // 12 hours
  // CU-868kpjgur: how long a login may hold a credential without `user`.
  // See markLoginAttempt() and hasPartialSession().
  private static LOGIN_ATTEMPT_TTL = 30 * 1000;

  static create(user: UserModel, token: string): void {
    this.put('user', user);
    this.put('token', token);
  }

  // CU-868kpjgur: a session needs the credential and the profile.
  //
  // addSessionData() writes userId, token, refreshToken and org. A later
  // request writes `user`. When that request failed, this method still
  // returned true. Each caller then read a null `user`.
  //
  // The AppComponent constructor raised a TypeError there. That rejected
  // bootstrapApplication and left app-root empty. The user saw a blank page.
  //
  // This method now requires `user`. That also corrects the three route
  // guards: CheckSessionActivator, InternalOnlyActivator and
  // WallDashboardActivator.
  static hasSession(): any{
    if (localStorage.getItem('userId') && localStorage.getItem('token') && localStorage.getItem('user')) {
      return true;
    }
    return false;
  }

  // CU-868kpjgur: a login records the moment it writes the credential.
  //
  // localStorage is shared by every tab on this origin. A running login in one
  // tab looks the same on disk as a login that failed: a credential, and no
  // `user`. This mark separates the two.
  static markLoginAttempt(): void {
    localStorage.setItem('loginAttemptAt', String(Date.now()));
  }

  // CU-868kpjgur: true when a login started, but did not finish. The app uses
  // this to tell a failed login from a first visit. It asks the user to sign
  // in again only after a failed login.
  //
  // Warning: this must stay false while another tab still runs a login.
  //
  // The caller clears the session on a true result, and Session.destroy()
  // calls localStorage.clear(), which empties every tab (claude[bot], PR
  // #765). A second tab that loads during a login would otherwise delete the
  // credential of the tab that is signing in.
  //
  // A recent mark means a login still runs, so this returns false. The mark
  // goes stale after LOGIN_ATTEMPT_TTL, and the app cleans up then. Waiting
  // costs the user nothing: hasSession() already rejects a session with no
  // `user`, so the app shows the login form either way.
  static hasPartialSession(): boolean {
    const hasCredential = !!(localStorage.getItem('token') || localStorage.getItem('userId'));
    if (!hasCredential || localStorage.getItem('user')) {
      return false;
    }
    const startedAt = Number(localStorage.getItem('loginAttemptAt'));
    if (startedAt && Date.now() - startedAt < Session.LOGIN_ATTEMPT_TTL) {
      return false;
    }
    return true;
  }

  static getLastVisitedUrl(): any{
    if (this.lastVisitedUrl){
      return this.lastVisitedUrl;
    }
    return '';
  }

  static setLastVisitedUrl(url: any): void{
    this.lastVisitedUrl = url;
  }

  static setCurrentCompany(companyId: any): any{
    localStorage.setItem('currentCompany', companyId);
  }

  static setCurrentCompanyName(companyName: any): any{
    localStorage.setItem('currentCompanyName', companyName);
  }

  static setCurrentCompanyCurrency(companyCurrency: any): any{
    localStorage.setItem('companyCurrency', companyCurrency);
  }

  static setCompanyReportCurrency(reportCurrency: any){
    localStorage.setItem('reportCurrency', reportCurrency);
  }

  static setFiscalStartDate(date: any){
    localStorage.setItem('fiscalStartDate', date);
  }

  static getCurrentCompany(){
    return localStorage.getItem('currentCompany');
  }

  static getCurrentCompanyName(){
    return localStorage.getItem('currentCompanyName');
  }

  static getCurrentCompanyCurrency(){
    return localStorage.getItem('companyCurrency');
  }

  static getCompanyReportCurrency(){
    return localStorage.getItem('reportCurrency');
  }

  static getFiscalStartDate(){
    return localStorage.getItem('fiscalStartDate');
  }

  static getUser(type?: any): UserModel {
    if (type == 'delegatedFrom'){
      const user = this.get(type);
      return user ? user : this.get('user');
    }
    return this.get('user');
  }

  static getToken(): any {
    return localStorage.getItem('token');
  }

  static setToken(token: any){
    localStorage.setItem('token', token);
    if (!localStorage.getItem('sessionStart')) {
      localStorage.setItem('sessionStart', String(Date.now()));
    }
  }

  static removeToken(){
    localStorage.removeItem('token');
  }

  static put(key: any, value: any) {
    if (key == 'token') {
      localStorage.setItem(key, value);
    }
    else {
      localStorage.setItem(key, JSON.stringify(value));
    }

  }

  static getKey(key: any): any {
    return localStorage.getItem(key) ? localStorage.getItem(key).replace(/"/g, '') : null;
  }
  static deleteKey(key: any) {
    return localStorage.removeItem(key);
  }

  static get(key: any) {
    return JSON.parse(localStorage.getItem(key));
  }

  static getJSON(key: any): any {
    return JSON.parse(localStorage.getItem(key));
  }

  static setVendor(vendorID: any){
    localStorage.setItem('vendorID', vendorID);
  }

  static getVendor(){
    return localStorage.getItem('vendorID');
  }

  static getReportCriteria(reportType: any){
    return JSON.parse(localStorage.getItem(reportType));
  }

  static setReportCriteria(reportType: any, criteria: any){
    localStorage.setItem(reportType, JSON.stringify(criteria));
  }

  static clearReportCriteria(reportType: any){
    localStorage.removeItem(reportType);
  }

  static getReportData(reportType: any){
    return JSON.parse(localStorage.getItem(reportType));
  }

  static setReportData(reportType: any, data: any){
    localStorage.setItem(reportType, JSON.stringify(data));
  }


  static getTTL(){
    return localStorage.getItem('ttl');
  }

  static setTTL(ttl: any){
    localStorage.setItem('ttl', ttl);
  }


  static isSessionExpired(): boolean {
    const start = localStorage.getItem('sessionStart');
    if (!start) return true;
    return Date.now() - Number(start) > Session.SESSION_TTL;
  }

  static resetSessionStart(): void {
    localStorage.setItem('sessionStart', String(Date.now()));
  }

  static getRefreshToken(){
    return localStorage.getItem('refreshToken');
  }

  static setRefreshToken(token: any){
    localStorage.setItem('refreshToken', token);
  }

  static getLockDate(){
    return localStorage.getItem('lockDate');
  }

  static setLockDate(lockDate: any){
    localStorage.setItem('lockDate', lockDate);
  }


  static clearReportData(reportType: any){
    localStorage.removeItem(reportType);
  }

  static setTimer(lockDate: any){
    localStorage.setItem('timer', lockDate);
  }

  static setCompanies(companies: any){
    localStorage.setItem('companies', JSON.stringify(companies));
  }

  static getCompanies(){
    return JSON.parse(localStorage.getItem('companies'));
  }

  static setAccessCompanies(accessCompanies: any){
    localStorage.setItem('accessCompanies', JSON.stringify(accessCompanies));
  }

  static getAccessCompanies(){
    return JSON.parse(localStorage.getItem('accessCompanies'));
  }

  static setUsers(users: any){
    localStorage.setItem('users', JSON.stringify(users));
  }

  static getUsers(){
    return JSON.parse(localStorage.getItem('users'));
  }

  static setProjects(projects: any){
    localStorage.setItem('projects', JSON.stringify(projects));
  }

  static getProjects(){
    return JSON.parse(localStorage.getItem('projects'));
  }

  static sessionRefreshTimer(interval: any){
    localStorage.setItem('sessionRefreshTimer', interval);
  }

  static setConversation(conversation: any){
    localStorage.setItem('conversation', JSON.stringify(conversation));
  }

  static getConversation(){
    return JSON.parse(localStorage.getItem('conversation'));
  }

  static setEnvironment(env: any){
    localStorage.setItem('env', env);
  }

  static getEnvironment(){
    return localStorage.getItem('env');
  }

  static setOrg(org: any){
    localStorage.setItem('org', org);
  }

  static getOrg(){
    return localStorage.getItem('org');
  }

  static setLoginSource(source: any): any{
    localStorage.setItem('loginsource', source);
  }

  static getLoginSource(): any{
    return localStorage.getItem('loginsource');
  }

  static setOrgCompany(orgCompany: any): any{
    localStorage.setItem('orgCompany', orgCompany);
  }

  static getOrgCompany(): any{
    return localStorage.getItem('orgCompany');
  }

  static setOrgCompanyDetails(company: any): any{
    localStorage.setItem('orgInfo', JSON.stringify(company));
  }

  static getOrgCompanyDetails(): any{
    return JSON.parse(localStorage.getItem('orgInfo'));
  }

  static setClientTypes(clientTypes: any): any{
    localStorage.setItem('clientTypes', JSON.stringify(clientTypes));
  }

  static getClientTypes(): any{
    return JSON.parse(localStorage.getItem('clientTypes'));
  }

  static setSnoozeStatus(snooze: any): any{
    localStorage.setItem('snoozeActive', snooze);
  }

  static isSnoozeActive(): any{
    return localStorage.getItem('snoozeActive');
  }

  static setPostLabels(labels: any): any{
    localStorage.setItem('postLabels', JSON.stringify(labels));
  }

  static getPostLabels(): any{
    return JSON.parse(localStorage.getItem('postLabels'));
  }


  static setUserPreferences(prefs: any): any{
    localStorage.setItem('userPreferences', JSON.stringify(prefs));
  }

  static getUserPreferences(): any{
    return JSON.parse(localStorage.getItem('userPreferences'));
  }

  static setTaxPlanOrgTerms(terms: any) {
    localStorage.setItem('taxPlanTerms', JSON.stringify(terms));
  }

  static getTaxPlanOrgTerms(): any{
    return JSON.parse(localStorage.getItem('taxPlanTerms'));
  }

  static destroy(): void {
    // @ts-ignore
    if (localStorage.getItem('timer')){
      clearInterval(Number(localStorage.getItem('timer')));
    }
    if (localStorage.getItem('sessionRefreshTimer')){
      clearInterval(Number(localStorage.getItem('sessionRefreshTimer')));
    }
    // localStorage.clear();
    localStorage.clear();
  }

  static setBadgeCount(badgeCount: any): any{
    // @ts-ignore
    localStorage.setItem('badgeCount', JSON.stringify(badgeCount));
  }

  static getBadgeCount(): any{
    // @ts-ignore
    return JSON.parse(localStorage.getItem('badgeCount'));
  }

  static setUserAccessInfo(list: any){
    localStorage.setItem('userAccessList', JSON.stringify(list));
  }

  static getUserAccessInfo(): any{
    return JSON.parse(localStorage.getItem('userAccessList'));
  }

  static setTaxPlanningProtectedView(view: any) {
    localStorage.setItem('protectedView', JSON.stringify(view));
  }

  static getTaxPlanningProtectedView(){
    return JSON.parse(localStorage.getItem('protectedView'));
  }

  static setTaxStrategyCurrentTab(tab: any){
    localStorage.setItem('strategyCurrentTab', JSON.stringify(tab));
  }

  static getTaxStrategyCurrentTab(): any {
    return JSON.parse(localStorage.getItem('strategyCurrentTab'));
  }

  static setClient(clientData: any){
    localStorage.setItem('client', JSON.stringify(clientData));
  }

  static getClient(): any {
    return JSON.parse(localStorage.getItem('client'));
  }

}
